import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Compass, Plus, Sparkles, Loader2, Target, Users, Swords, Layers, Save, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useProject } from '@/context/ProjectContext';
import { toast } from 'sonner';
import ProviderBadge from '@/components/ProviderBadge';
import ReportButton from '@/components/ReportButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MAX_TONE_ITEMS = 8;

const StrategyPanel = () => {
  const { currentProject } = useProject();

  const [audience, setAudience] = useState('');
  const [competitorInput, setCompetitorInput] = useState('');
  const [competitors, setCompetitors] = useState([]);
  const [goals, setGoals] = useState('');
  const [budget, setBudget] = useState('');
  const [positioningNotes, setPositioningNotes] = useState('');
  const [strategy, setStrategy] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [fetching, setFetching] = useState(false);

  // BP-01-A: Tone of Voice
  const [dos, setDos] = useState([]);
  const [donts, setDonts] = useState([]);
  const [dosInput, setDosInput] = useState('');
  const [dontsInput, setDontsInput] = useState('');
  const [toneSaving, setToneSaving] = useState(false);

  const fetchStrategy = async () => {
    if (!currentProject) return;
    try {
      setFetching(true);
      const res = await axios.get(`${API}/projects/${currentProject.id}/strategy`);
      setStrategy(res.data);
    } catch (error) {
      console.error('Failed to fetch strategy:', error);
    } finally {
      setFetching(false);
    }
  };

  const fetchTone = async () => {
    if (!currentProject) return;
    try {
      const res = await axios.get(`${API}/projects/${currentProject.id}/tone`);
      if (res.data) {
        setDos(res.data.dos || []);
        setDonts(res.data.donts || []);
      }
    } catch {
      // Tone not configured yet
    }
  };

  useEffect(() => {
    fetchStrategy();
    fetchTone();
    if (currentProject) {
      setPositioningNotes(currentProject.description || '');
    }
  }, [currentProject?.id]);

  const addCompetitor = () => {
    const value = competitorInput.trim();
    if (value && !competitors.includes(value)) {
      setCompetitors([...competitors, value]);
      setCompetitorInput('');
    }
  };

  const removeCompetitor = (value) => {
    setCompetitors(competitors.filter((c) => c !== value));
  };

  const addDo = () => {
    const val = dosInput.trim();
    if (val && dos.length < MAX_TONE_ITEMS && !dos.includes(val)) {
      setDos([...dos, val]);
      setDosInput('');
    }
  };

  const removeDo = (val) => setDos(dos.filter((d) => d !== val));

  const addDont = () => {
    const val = dontsInput.trim();
    if (val && donts.length < MAX_TONE_ITEMS && !donts.includes(val)) {
      setDonts([...donts, val]);
      setDontsInput('');
    }
  };

  const removeDont = (val) => setDonts(donts.filter((d) => d !== val));

  const handleSaveTone = async () => {
    try {
      setToneSaving(true);
      await axios.patch(`${API}/projects/${currentProject.id}/tone`, { dos, donts });
      toast.success('Tone of voice saved');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Failed to save tone of voice');
    } finally {
      setToneSaving(false);
    }
  };

  const handleGenerate = async () => {
    if (!audience.trim() || !goals.trim()) {
      toast.error('Describe the target audience and goals first');
      return;
    }
    try {
      setGenerating(true);
      const res = await axios.post(`${API}/projects/${currentProject.id}/strategy/generate`, {
        audience,
        competitors,
        goals,
        budget: budget || undefined,
        positioning_notes: positioningNotes || undefined,
      });
      setStrategy(res.data);
      toast.success('Brand strategy generated');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Failed to generate strategy');
    } finally {
      setGenerating(false);
    }
  };

  if (!currentProject) return null;

  const ChipInput = ({ chips, onAdd, onRemove, inputValue, onInputChange, placeholder }) => (
    <div>
      <div className="flex flex-wrap gap-2 mb-2">
        {chips.map((item, i) => (
          <span
            key={i}
            className="flex items-center gap-1.5 px-3 py-1 bg-primary/5 border border-primary/20 text-sm text-foreground"
          >
            {item}
            <button
              type="button"
              onClick={() => onRemove(item)}
              className="text-muted-foreground hover:text-red-400 transition-colors"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      {chips.length < MAX_TONE_ITEMS && (
        <div className="flex gap-2">
          <Input
            value={inputValue}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), onAdd())}
            placeholder={placeholder}
            className="bg-white/[0.05] border-border text-foreground placeholder:text-muted-foreground/70"
          />
          <Button
            type="button"
            onClick={onAdd}
            variant="outline"
            className="bg-white/[0.05] border-border text-foreground hover:bg-white/[0.08] shrink-0"
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      )}
      {chips.length >= MAX_TONE_ITEMS && (
        <p className="text-xs text-muted-foreground italic">Maximum {MAX_TONE_ITEMS} items reached.</p>
      )}
    </div>
  );

  return (
    <div className="h-full overflow-auto p-6" data-testid="strategy-panel">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="mb-6 flex items-start justify-between gap-4">
            <div>
              <h2 className="font-heading text-2xl text-foreground mb-2">Strategy</h2>
              <p className="text-muted-foreground">Turn intake into a real positioning statement, personas, and content direction</p>
            </div>
            <ReportButton section="strategy" projectId={currentProject.id} />
          </div>

          {/* Intake Section */}
          <div className="glass rounded-2xl p-6 border border-border mb-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Compass className="w-5 h-5 text-primary" />
              </div>
              <h3 className="font-heading text-lg text-foreground">Intake</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
              <div className="space-y-2 md:col-span-2">
                <Label className="text-foreground/80">Target audience</Label>
                <textarea
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  placeholder="e.g., Remote software developers, 25-40, who value focus and minimalism over convenience"
                  rows={2}
                  className="w-full rounded-md bg-white/[0.05] border border-border text-foreground placeholder:text-muted-foreground/70 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
                  data-testid="strategy-audience-input"
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label className="text-foreground/80">Goals</Label>
                <textarea
                  value={goals}
                  onChange={(e) => setGoals(e.target.value)}
                  placeholder="e.g., Launch a subscription product, build a loyal repeat-purchase audience within a year"
                  rows={2}
                  className="w-full rounded-md bg-white/[0.05] border border-border text-foreground placeholder:text-muted-foreground/70 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
                  data-testid="strategy-goals-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-foreground/80">Budget (optional)</Label>
                <Input
                  value={budget}
                  onChange={(e) => setBudget(e.target.value)}
                  placeholder="e.g., Bootstrapped, under $5k/mo"
                  className="bg-white/[0.05] border-border text-foreground placeholder:text-muted-foreground/70"
                  data-testid="strategy-budget-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-foreground/80">Competitors (optional)</Label>
                <div className="flex gap-2">
                  <Input
                    value={competitorInput}
                    onChange={(e) => setCompetitorInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCompetitor())}
                    placeholder="e.g., Blue Bottle"
                    className="bg-white/[0.05] border-border text-foreground placeholder:text-muted-foreground/70"
                    data-testid="strategy-competitor-input"
                  />
                  <Button
                    onClick={addCompetitor}
                    variant="outline"
                    className="bg-white/[0.05] border-border text-foreground hover:bg-white/[0.08]"
                    data-testid="strategy-add-competitor-btn"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label className="text-foreground/80">Positioning notes (optional)</Label>
                <textarea
                  value={positioningNotes}
                  onChange={(e) => setPositioningNotes(e.target.value)}
                  placeholder="Anything you already know you want to lead with or avoid"
                  rows={2}
                  className="w-full rounded-md bg-white/[0.05] border border-border text-foreground placeholder:text-muted-foreground/70 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
                  data-testid="strategy-positioning-input"
                />
              </div>
            </div>

            {competitors.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {competitors.map((c, index) => (
                  <span
                    key={index}
                    className="px-3 py-1.5 rounded-full bg-white/[0.05] border border-border text-sm text-foreground flex items-center gap-2 group"
                  >
                    {c}
                    <button
                      onClick={() => removeCompetitor(c)}
                      className="text-muted-foreground hover:text-red-400 transition-colors"
                      data-testid={`strategy-remove-competitor-${index}`}
                    >
                      &times;
                    </button>
                  </span>
                ))}
              </div>
            )}

            <Button
              onClick={handleGenerate}
              disabled={generating}
              className="bg-primary text-primary-foreground font-semibold hover:bg-primary/90 disabled:opacity-50"
              data-testid="strategy-generate-btn"
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  {strategy ? 'Regenerate Strategy' : 'Generate Strategy'}
                </>
              )}
            </Button>
          </div>

          {/* Results */}
          {fetching && !strategy ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 text-primary animate-spin" />
            </div>
          ) : !strategy ? (
            <div className="glass rounded-2xl p-10 border border-border text-center">
              <Compass className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" />
              <p className="text-muted-foreground">No strategy yet. Fill in the intake above and generate one.</p>
            </div>
          ) : (
            <div className="space-y-6" data-testid="strategy-results">
              {/* Positioning statement */}
              <div className="glass rounded-2xl p-6 border border-border">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Target className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-heading text-lg text-foreground flex-1">Positioning</h3>
                  <ProviderBadge provider={strategy.generated_by} />
                </div>
                <p className="text-foreground text-lg leading-relaxed font-heading">{strategy.positioning_statement}</p>
              </div>

              {/* Personas */}
              {strategy.personas?.length > 0 && (
                <div className="glass rounded-2xl p-6 border border-border">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-secondary/10 flex items-center justify-center">
                      <Users className="w-5 h-5 text-secondary" />
                    </div>
                    <h3 className="font-heading text-lg text-foreground">Personas</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {strategy.personas.map((persona, i) => (
                      <div key={i} className="rounded-xl border border-border bg-white/[0.03] p-4">
                        <h4 className="font-heading text-foreground mb-1">{persona.name}</h4>
                        <p className="text-sm text-muted-foreground mb-3">{persona.description}</p>
                        {persona.pain_points?.length > 0 && (
                          <div className="mb-2">
                            <span className="text-xs uppercase tracking-wide text-muted-foreground/70">Pain points</span>
                            <ul className="text-sm text-foreground list-disc list-inside">
                              {persona.pain_points.map((p, j) => <li key={j}>{p}</li>)}
                            </ul>
                          </div>
                        )}
                        {persona.goals?.length > 0 && (
                          <div>
                            <span className="text-xs uppercase tracking-wide text-muted-foreground/70">Goals</span>
                            <ul className="text-sm text-foreground list-disc list-inside">
                              {persona.goals.map((g, j) => <li key={j}>{g}</li>)}
                            </ul>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Competitive landscape */}
              {strategy.competitive_landscape?.length > 0 && (
                <div className="glass rounded-2xl p-6 border border-border">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
                      <Swords className="w-5 h-5 text-red-400" />
                    </div>
                    <h3 className="font-heading text-lg text-foreground">Competitive Landscape</h3>
                  </div>
                  <div className="space-y-3">
                    {strategy.competitive_landscape.map((c, i) => (
                      <div key={i} className="rounded-xl border border-border bg-white/[0.03] p-4">
                        <h4 className="font-heading text-foreground mb-1">{c.name}</h4>
                        <p className="text-sm text-muted-foreground"><span className="text-foreground/80">Strengths:</span> {c.strengths}</p>
                        <p className="text-sm text-muted-foreground"><span className="text-foreground/80">Weaknesses:</span> {c.weaknesses}</p>
                        <p className="text-sm text-muted-foreground"><span className="text-foreground/80">Differentiation:</span> {c.differentiation}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Content pillars + suggested tone/keywords */}
              <div className="glass rounded-2xl p-6 border border-border">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Layers className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-heading text-lg text-foreground">Content Pillars</h3>
                </div>
                <div className="flex flex-wrap gap-2 mb-4">
                  {strategy.content_pillars?.map((pillar, i) => (
                    <span key={i} className="px-3 py-1.5 rounded-full bg-white/[0.05] border border-border text-sm text-foreground">
                      {pillar}
                    </span>
                  ))}
                </div>
                {(strategy.suggested_tone_of_voice || strategy.suggested_keywords?.length > 0) && (
                  <p className="text-xs text-muted-foreground border-t border-border pt-3">
                    Suggested tone &amp; keywords have been merged into your Brand Kit automatically (only where those fields were still empty).
                  </p>
                )}
              </div>

              {/* BP-01-A: Tone of Voice — only visible once strategy exists */}
              <div className="glass rounded-2xl p-6 border border-border" data-testid="tone-of-voice-section">
                <div className="mb-5">
                  <p className="font-mono text-xs uppercase tracking-wide lodge-accent mb-1">Tone of Voice (BP-01-A)</p>
                  <p className="text-sm text-muted-foreground italic">
                    These rules are injected into every AI generation call for this project — social posts, website copy, reports.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-5">
                  <div className="space-y-2">
                    <Label className="text-foreground/80">Do's</Label>
                    <ChipInput
                      chips={dos}
                      onAdd={addDo}
                      onRemove={removeDo}
                      inputValue={dosInput}
                      onInputChange={setDosInput}
                      placeholder="e.g., Use active voice"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-foreground/80">Don'ts</Label>
                    <ChipInput
                      chips={donts}
                      onAdd={addDont}
                      onRemove={removeDont}
                      inputValue={dontsInput}
                      onInputChange={setDontsInput}
                      placeholder="e.g., No exclamation marks"
                    />
                  </div>
                </div>

                <Button
                  onClick={handleSaveTone}
                  disabled={toneSaving}
                  className="bg-primary text-primary-foreground font-semibold hover:bg-primary/90 disabled:opacity-50"
                  data-testid="tone-save-btn"
                >
                  {toneSaving ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>
                  ) : (
                    <><Save className="w-4 h-4 mr-2" />Save Tone Rules</>
                  )}
                </Button>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default StrategyPanel;
